import {
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  StopCircleIcon,
} from "@heroicons/react/24/outline";
import Modal from "@via/components/modal.tsx";
import { Spinner, OverlayLoading } from "@via/components/loading/loading.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import Config from "@via/utils/config";
import {
  browserHref,
  isNavigableDownload,
  isScriptable,
  needsStreamAuth,
  unixDownloadCommand,
  windowsDownloadCommand,
  windowsMkdirCommand,
  commentText,
  safeRelativePath,
  NO_SCRIPTABLE_LINKS,
  STREAM_AUTH_NOTE_UNIX,
  STREAM_AUTH_NOTE_WINDOWS,
} from "./download-script";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { type RunReportDownloadUrlResponse } from "common";
import { type FC, useState, useMemo, useRef, useEffect } from "react";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import { HeadingSize, PageHeading } from "@via/components/page-heading";
import {
  MessageBanner,
  BannerType,
  BannerSize,
} from "@via/components/message-banner/message-banner.tsx";
import {
  type DownloadStatus,
  type DownloadProgress,
  getDownloadStatusIcon,
  getDownloadStatusText,
} from "@via/pages/projects/runs/downloads/download-utils.tsx";

// Re-export types for backwards compatibility
export type { DownloadStatus, DownloadProgress };

interface DownloadProgressModalProps {
  open: boolean;
  onClose: () => void;
  downloadUrls: RunReportDownloadUrlResponse[];
  onDownloadComplete?: (completedFileNames?: string[]) => void;
  onDownloadStateChange?: (isRunDownloading: boolean) => void;
  onRegenerateUrls?: (usePrefixedFilenames: boolean) => Promise<void>;
  onGenerateScriptUrls?: (
    usePrefixedFilenames: boolean
  ) => Promise<RunReportDownloadUrlResponse[] | null>;
  isRegeneratingUrls?: boolean;
}

type ScriptType = "Unix/Linux/macOS" | "Windows (PowerShell)";

const DownloadProgressModal: FC<DownloadProgressModalProps> = ({
  open,
  onClose,
  downloadUrls,
  onDownloadComplete,
  onDownloadStateChange,
  onRegenerateUrls,
  onGenerateScriptUrls,
  isRegeneratingUrls = false,
}) => {
  const [scriptType, setScriptType] = useState<ScriptType>("Unix/Linux/macOS");
  const [copied, setCopied] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress[]>(
    []
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [usePrefixedFilenames, setUsePrefixedFilenames] = useState(true);
  const [scriptUrls, setScriptUrls] = useState<
    RunReportDownloadUrlResponse[] | null
  >(null);
  const cancelRef = useRef(false);
  const isRegeneratingRef = useRef(false); // Guard against rapid checkbox toggling
  const { setNotification, NOTIFICATION } = useNotifications();

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setUsePrefixedFilenames(true);
      setScriptUrls(null);
    }
  }, [open]);

  // Handle prefixed filenames checkbox toggle
  const handlePrefixedFilenamesChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Guard against race conditions from rapid toggling
    if (isRegeneratingRef.current) return;

    const newValue = e.target.checked;
    const previousValue = usePrefixedFilenames;
    setUsePrefixedFilenames(newValue);
    setScriptUrls(null);

    if (onRegenerateUrls) {
      isRegeneratingRef.current = true;
      try {
        await onRegenerateUrls(newValue);

        if (onGenerateScriptUrls) {
          const urls = await onGenerateScriptUrls(newValue);
          if (urls) setScriptUrls(urls);
        }
      } catch (error) {
        // Revert checkbox state if regeneration fails
        setUsePrefixedFilenames(previousValue);
      } finally {
        isRegeneratingRef.current = false;
      }
    }
  };

  const ensureScriptUrls = async (): Promise<
    RunReportDownloadUrlResponse[] | null
  > => {
    if (scriptUrls) return scriptUrls;
    if (!onGenerateScriptUrls) return downloadUrls;

    try {
      const urls = await onGenerateScriptUrls(usePrefixedFilenames);
      if (urls) {
        setScriptUrls(urls);
        return urls;
      }
      return downloadUrls;
    } catch {
      return downloadUrls;
    }
  };

  // Check if downloads include direct downloads (S3/GCS) or only proxy downloads (SSH)
  const hasDirectDownloads = useMemo(
    () => downloadUrls.some(isScriptable),
    [downloadUrls]
  );

  const getCompletedCount = () => {
    return downloadProgress.filter(item => item.status === "completed").length;
  };

  // Helper function to extract relative path from URL
  // URL format: https://storage.googleapis.com/bucket/report12639/[subDir/]file.ext
  // or: https://s3.amazonaws.com/bucket/run12639/[subDir/]file.ext
  // We want to preserve the relative path including report{id}/ or run{id}/ prefix
  const extractRelativePath = (url: string, fileName: string): string => {
    let relativePath = fileName;
    try {
      const urlObj = new URL(url);
      const pathname = decodeURIComponent(urlObj.pathname);
      // Find report{id} or run{id} folder and everything after it
      const match = pathname.match(/\/((?:report|run)\d+\/.*)$/);
      if (match) {
        relativePath = match[1];
      }
    } catch (e) {
      // If URL parsing fails, fall back to fileName
      console.warn("Failed to parse URL:", e);
    }
    // Names are data from the bucket: refuse traversal, absolute and drive paths.
    return safeRelativePath(relativePath, fileName);
  };

  const getScriptUrls = (): RunReportDownloadUrlResponse[] => {
    return scriptUrls ?? downloadUrls;
  };

  const generateUnixScript = (
    urls: RunReportDownloadUrlResponse[] = getScriptUrls()
  ) => {
    const header =
      "# Download script for Unix/Linux/macOS\n# Generated by Foundry Connect\n\n";

    const directDownloads = urls.filter(isScriptable);

    if (directDownloads.length === 0) {
      return header + NO_SCRIPTABLE_LINKS;
    }

    const commands = directDownloads
      .map((url, index) => {
        const relativePath = extractRelativePath(
          url.path ?? url.url,
          url.fileName
        );
        return `# File ${index + 1}: ${commentText(relativePath)}\n${unixDownloadCommand(url, relativePath, window.location.origin, Config.ROUTER_BASENAME ?? "")}`;
      })
      .join("\n\n");
    const authNote = needsStreamAuth(directDownloads)
      ? STREAM_AUTH_NOTE_UNIX
      : "";
    return header + authNote + commands;
  };

  const generateWindowsScript = (
    urls: RunReportDownloadUrlResponse[] = getScriptUrls()
  ) => {
    const header =
      "# Download script for Windows PowerShell\n# Generated by Foundry Connect\n\n";

    const directDownloads = urls.filter(isScriptable);

    if (directDownloads.length === 0) {
      return header + NO_SCRIPTABLE_LINKS;
    }

    const commands = directDownloads
      .map((url, index) => {
        const relativePath = extractRelativePath(
          url.path ?? url.url,
          url.fileName
        );
        // Convert to Windows path
        const winFilePath = relativePath.replace(/\//g, "\\");
        // PowerShell: Create parent directory if path contains subdirectories
        const mkdirCmd = windowsMkdirCommand(winFilePath);
        return `# File ${index + 1}: ${commentText(relativePath)}\n${mkdirCmd}${windowsDownloadCommand(url, winFilePath, window.location.origin, Config.ROUTER_BASENAME ?? "")}`;
      })
      .join("\n\n");
    const authNote = needsStreamAuth(directDownloads)
      ? STREAM_AUTH_NOTE_WINDOWS
      : "";
    return header + authNote + commands;
  };

  const handleCopyScript = async () => {
    const urls = (await ensureScriptUrls()) ?? getScriptUrls();
    const script =
      scriptType === "Unix/Linux/macOS"
        ? generateUnixScript(urls)
        : generateWindowsScript(urls);
    if (navigator?.clipboard) {
      try {
        await navigator.clipboard.writeText(script);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy script:", err);
      }
    }
  };

  const handleDownloadScript = async (type: ScriptType) => {
    const urls = (await ensureScriptUrls()) ?? getScriptUrls();
    setScriptType(type);
    const isUnix = type === "Unix/Linux/macOS";
    const script = isUnix
      ? generateUnixScript(urls)
      : generateWindowsScript(urls);
    const extension = isUnix ? "sh" : "ps1";

    const blob = new Blob([script], { type: "text/plain" });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `download-files.${extension}`;
    link.click();
    window.URL.revokeObjectURL(blobUrl);
  };

  const startDirectDownload = async () => {
    setIsDownloading(true);
    setIsCancelling(false);
    cancelRef.current = false;
    onDownloadStateChange?.(true); // Notify parent that download has started

    try {
      // Initialize download progress for all files
      const initialProgress: DownloadProgress[] = downloadUrls.map(urlInfo => ({
        fileName: urlInfo.fileName,
        status: "pending" as DownloadStatus,
      }));
      setDownloadProgress(initialProgress);

      // Download files sequentially
      let successCount = 0;
      let failCount = 0;
      let cancelledCount = 0;
      const completedFileNames: string[] = [];

      for (const [i, urlInfo] of downloadUrls.entries()) {
        // Check if cancellation was requested
        if (cancelRef.current) {
          // Mark remaining files as cancelled
          setDownloadProgress(prev =>
            prev.map((item, idx) =>
              idx >= i ? { ...item, status: "cancelled" } : item
            )
          );
          cancelledCount = downloadUrls.length - i;
          break;
        }

        // Update status to downloading
        setDownloadProgress(prev =>
          prev.map((item, idx) =>
            idx === i ? { ...item, status: "downloading" } : item
          )
        );

        try {
          if (isNavigableDownload(urlInfo)) {
            // Direct or stream download using anchor tag (no CORS issues)
            // Note: Browsers don't support directory creation, so we use just the filename
            const fileName = urlInfo.fileName.includes("/")
              ? urlInfo.fileName.substring(
                  urlInfo.fileName.lastIndexOf("/") + 1
                )
              : urlInfo.fileName;

            const link = document.createElement("a");
            link.href = browserHref(urlInfo, Config.ROUTER_BASENAME ?? "");
            link.download = fileName;
            link.style.display = "none";
            document.body.appendChild(link);

            // Ensure DOM operations complete before clicking
            await new Promise(resolve => requestAnimationFrame(resolve));
            link.click();

            // Clean up DOM synchronously after next frame
            await new Promise(resolve => requestAnimationFrame(resolve));
            document.body.removeChild(link);

            // Delay between downloads to prevent browser blocking
            // Browsers typically block rapid consecutive downloads
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else if (urlInfo.method === "proxy") {
            // Proxy download (for SSH files)
            // Note: Browsers don't support directory creation, so we use just the filename
            const fileName = urlInfo.fileName.includes("/")
              ? urlInfo.fileName.substring(
                  urlInfo.fileName.lastIndexOf("/") + 1
                )
              : urlInfo.fileName;

            const response = await fetch(urlInfo.url);
            if (!response.ok) {
              throw new Error(`Failed to download ${urlInfo.fileName}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            link.style.display = "none";
            document.body.appendChild(link);

            // Ensure DOM operations complete before clicking
            await new Promise(resolve => requestAnimationFrame(resolve));
            link.click();

            // Clean up DOM and blob URL synchronously after next frame
            await new Promise(resolve => requestAnimationFrame(resolve));
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            // Delay between downloads to prevent browser blocking
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          // Update status to completed
          setDownloadProgress(prev =>
            prev.map((item, idx) =>
              idx === i ? { ...item, status: "completed" } : item
            )
          );
          successCount++;
          completedFileNames.push(urlInfo.fileName);
        } catch (error) {
          console.error(`Failed to download ${urlInfo.fileName}:`, error);
          // Update status to failed
          setDownloadProgress(prev =>
            prev.map((item, idx) =>
              idx === i
                ? {
                    ...item,
                    status: "failed",
                    error:
                      error instanceof Error ? error.message : "Unknown error",
                  }
                : item
            )
          );
          failCount++;
        }
      }

      // Show completion notification and handle cart cleanup
      if (cancelledCount > 0) {
        setNotification({
          id: "download-cancelled",
          message: "Downloads Cancelled",
          details: `${successCount} file${successCount !== 1 ? "s" : ""} initiated, ${cancelledCount} cancelled. Check your browser's downloads folder for initiated files.`,
          variant: NOTIFICATION.WARNING,
        });
        // Remove only successfully initiated files from cart
        if (completedFileNames.length > 0) {
          onDownloadComplete?.(completedFileNames);
        }
      } else if (successCount > 0 && failCount === 0) {
        setNotification({
          id: "download-complete",
          message: "Downloads Initiated",
          details: `Successfully initiated ${successCount} download${successCount > 1 ? "s" : ""}. Please check your browser's downloads folder.`,
          variant: NOTIFICATION.SUCCESS,
        });
        // Clear entire cart on successful download (no parameter = clear all)
        onDownloadComplete?.();
      } else if (successCount > 0 && failCount > 0) {
        setNotification({
          id: "download-partial",
          message: "Partial Download",
          details: `Initiated ${successCount} download${successCount > 1 ? "s" : ""}, ${failCount} failed. Check your browser's downloads folder.`,
          variant: NOTIFICATION.WARNING,
        });
        // Remove only successfully initiated files from cart
        if (completedFileNames.length > 0) {
          onDownloadComplete?.(completedFileNames);
        }
      } else {
        setNotification({
          id: "download-failed",
          message: "Download Failed",
          details:
            "Failed to initiate downloads. Please try again or use the download script.",
          variant: NOTIFICATION.ERROR,
        });
      }
    } catch (error) {
      console.error("Download error:", error);
      setNotification({
        id: "download-error",
        message: "Download Error",
        details:
          "An error occurred while initiating the download. Please try again.",
        variant: NOTIFICATION.ERROR,
      });
    } finally {
      setIsDownloading(false);
      setIsCancelling(false);
      cancelRef.current = false;
      onDownloadStateChange?.(false); // Notify parent that download has ended
    }
  };

  const handleCancel = () => {
    if (isDownloading && !isCancelling) {
      // Cancel the downloads
      setIsCancelling(true);
      cancelRef.current = true;
    } else if (!isDownloading) {
      // Close the modal
      onClose();
      // Reset states after modal closes
      setTimeout(() => {
        setDownloadProgress([]);
        setScriptType("Unix/Linux/macOS");
        setCopied(false);
        setShowScript(false);
        setScriptUrls(null);
        setIsCancelling(false);
        cancelRef.current = false;
      }, 300);
    }
  };

  const showScripts = downloadUrls.length > 0 && downloadProgress.length === 0;

  const modalTitle = (
    <div className="mb-4 flex items-center justify-between pr-8">
      <h3 className="text-lg font-medium leading-6 text-gray-900">
        {showScripts ? "Download Files" : "Download Progress"}
      </h3>
      {!showScripts && (
        <div className="text-sm text-gray-600">
          {getCompletedCount()} of {downloadProgress.length} initiated
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      setOpen={handleCancel}
      title={modalTitle}
      size={ModalSize.Large}
      hasCancelButton={true}
      cancelText={isDownloading && !isCancelling ? "Cancel" : "Close"}
      onCancel={handleCancel}
      disableClose={isDownloading && isCancelling}
      minHeight={300}>
      {showScripts ? (
        <>
          {/* Download Options UI */}
          <div className="space-y-6 pb-4">
            <p className="text-sm text-gray-700">
              Choose how you want to download the files:
            </p>
            <MessageBanner
              messages="Download links expire in 1 day. If expired, close this modal and initiate download again to generate new links."
              type={BannerType.Warning}
              title="Note"
              size={BannerSize.Small}
            />

            {/* Option A: Direct Browser Download */}
            <div className="px-4 py-3 shadow-md ring-1 ring-gray-900/5 rounded-lg">
              <div className="mb-3">
                <PageHeading
                  title={`${hasDirectDownloads ? "Option A: " : ""}Direct Browser Download`}
                  size={HeadingSize.XSmall}
                />
                <p className="mt-2 text-sm text-gray-600">
                  Download all files directly to your browser&apos;s default
                  download directory.
                </p>
                {hasDirectDownloads && (
                  <p className="mt-1 text-sm font-bold text-gray-900">
                    Note: Folder structure will not be preserved.
                  </p>
                )}
              </div>

              {/* Prefixed filenames checkbox */}
              <div className="relative mb-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                {isRegeneratingUrls && <OverlayLoading relative />}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={usePrefixedFilenames}
                    onChange={handlePrefixedFilenamesChange}
                    disabled={isRegeneratingUrls || isDownloading}
                    className="mt-1 h-6 w-6 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900">
                      Add subfolders as filename prefix
                    </span>
                    <p className="mt-1 text-xs text-gray-500">
                      Prevents filename conflicts by adding folder path as
                      prefix.
                    </p>
                    <p className="mt-1 text-xs text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                      Example: <span className="text-gray-400">multiqc/</span>
                      multiqc.html →{" "}
                      <span className="text-indigo-600 font-semibold">
                        multiqc_multiqc.html
                      </span>
                    </p>
                  </div>
                </label>
              </div>

              <button
                onClick={startDirectDownload}
                disabled={isDownloading || isRegeneratingUrls}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                <span className="w-5 h-5 flex items-center justify-center">
                  {isDownloading || isRegeneratingUrls ? (
                    <Spinner loading />
                  ) : (
                    <ArrowDownTrayIcon className="w-5 h-5" />
                  )}
                </span>
                {hasDirectDownloads
                  ? "Download Files Only (No Folder Structure)"
                  : "Download All Files"}
              </button>
            </div>

            {hasDirectDownloads && (
              <>
                {/* Option B: Download with Script */}
                <div className="px-4 py-3 shadow-md ring-1 ring-gray-900/5 rounded-lg">
                  <div className="mb-3">
                    <div className="flex items-center justify-between">
                      <PageHeading
                        title="Option B: Download with Script (Preserves Folder Structure)"
                        size={HeadingSize.XSmall}
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      Download and run the script to download all files while
                      keeping the folder structure.
                    </p>
                  </div>

                  <div className="flex gap-3 mb-3">
                    <button
                      onClick={() => handleDownloadScript("Unix/Linux/macOS")}
                      disabled={isRegeneratingUrls}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                      <ArrowDownTrayIcon className="w-5 h-5" />
                      Download Script (Unix/Linux/macOS)
                    </button>
                    <button
                      onClick={() =>
                        handleDownloadScript("Windows (PowerShell)")
                      }
                      disabled={isRegeneratingUrls}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                      <ArrowDownTrayIcon className="w-5 h-5" />
                      Download Script (Windows/PowerShell)
                    </button>
                  </div>

                  {/* Collapsible Show Script Section */}
                  <div className="border-t border-gray-200 pt-3">
                    <button
                      onClick={async () => {
                        if (!showScript) await ensureScriptUrls();
                        setShowScript(!showScript);
                      }}
                      disabled={isRegeneratingUrls}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50">
                      {isRegeneratingUrls && !showScript ? (
                        <Spinner loading />
                      ) : showScript ? (
                        <>
                          <ChevronUpIcon className="h-4 w-4" />
                          Hide Script
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="h-4 w-4" />
                          Show Script
                        </>
                      )}
                    </button>

                    {showScript && (
                      <div className="mt-3 space-y-3">
                        {/* Tab Buttons */}
                        <HorizontalTabs
                          options={[
                            { name: "Unix/Linux/macOS" as ScriptType },
                            { name: "Windows (PowerShell)" as ScriptType },
                          ]}
                          activeTab={scriptType}
                          onTabChange={setScriptType}
                        />

                        {/* Script Content */}
                        <div className="relative">
                          <textarea
                            readOnly
                            value={
                              scriptType === "Unix/Linux/macOS"
                                ? generateUnixScript()
                                : generateWindowsScript()
                            }
                            className="w-full h-64 font-mono text-xs p-3 bg-gray-900 text-gray-100 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <button
                            onClick={handleCopyScript}
                            className="absolute top-2 right-2 flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition">
                            {copied ? (
                              <>
                                <CheckIcon className="w-4 h-4" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <ClipboardDocumentIcon className="w-4 h-4" />
                                Copy Script
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Progress UI */}
          <div className="space-y-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {isCancelling ? (
                  <span className="flex items-center gap-2 text-red-600">
                    <StopCircleIcon className="h-5 w-5" />
                    Cancelling downloads...
                  </span>
                ) : isDownloading ? (
                  <span className="flex items-center gap-2">
                    <ArrowDownTrayIcon className="h-5 w-5 animate-bounce text-indigo-600" />
                    Initiating downloads...
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-green-600">
                    <CheckCircleIcon className="h-5 w-5" />
                    All downloads initiated - check your browser&apos;s download
                    folder
                  </span>
                )}
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <div className="space-y-3">
                {downloadProgress.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="flex-shrink-0">
                      {getDownloadStatusIcon(item.status)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-gray-900">
                        {item.fileName}
                      </div>
                      {item.error && (
                        <div className="mt-1 text-xs text-red-600">
                          {item.error}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-gray-500">
                        {getDownloadStatusText(item.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default DownloadProgressModal;
